// ─── L'agenda che si sfoglia ─────────────────────────────────────────────────
//
// 🎾⭐⭐ **PERCHÉ NON È PIÙ «UN GIORNO CON LE FRECCE».**
//
// Fino al 20/09 la schermata mostrava **un** giorno e si cambiava con ‹ ›: la
// domanda a cui rispondeva era *«adesso cosa ho»*, e per quella bastava. Le due
// che restavano senza risposta sono quelle che un maestro fa ogni giorno:
//   · *«com'è messa la settimana»* — con le frecce si scopre premendo sette
//     volte, cioè non si scopre;
//   · *«chi c'era lunedì»* — il passato non era raggiungibile in nessun modo, e
//     correggere chi c'era in campo è mezzo mestiere di chi tiene un registro.
//
// ⇒ Adesso i giorni sono **quattordici in fila** — sette indietro e sei avanti —
//   e in cima c'è un **datario** che dice quanto è pieno ciascuno.
//
// 🔄⭐⭐ **E DAL 23/09 (voce 13) SONO TRENTUNO, e l'elenco non li mostra tutti.**
// 🗣️ *«facciamo 15 precedenti e 15 successivi, e quando apro l'app si apre sul
// giorno attuale»* · *«nel calendario vedevi tutte e solamente le tue
// prenotazioni, non vedevi i giorni vuoti»*.
// ⇒ Il **datario** ha tutti i trentuno giorni — è un calendario, e un buco nella
//   striscia farebbe credere che quel giorno non esista. L'**elenco** invece ha
//   solo i giorni in cui c'è qualcosa, più **oggi**, sempre: è lì che si apre.
//   Toccando nel datario un giorno vuoto, lo si dice (⇒ `avviso-giorno`).
// ⇒ E la barra in basso non c'è più: si prenota dal «+» (⇒ `CosaPrenoti`).
// 🔄 Dal 23/09 (voce 16) non ci sono più nemmeno le tre righe in fondo: «I
//   soldi» si apre dall'«€», a sinistra del «+», e le assenze dal «+».
//
// 🕰️🚨 **E «OGGI» LO DICE IL SERVER, non questo browser** (⇒ `miaAgendaSchema`).
// Il giorno da cui la pagina si apre e la riga marcata «Oggi» vengono da
// `adesso`, che è l'orologio del circolo. Il browser sceglie soltanto **dove
// comincia la finestra**, e se il suo orologio fosse sbagliato di un giorno la
// finestra scivolerebbe di uno — contenendo oggi lo stesso, perché è larga
// quattordici e oggi sta all'ottavo posto.
// 📌 *Due orologi danno due verità: quella che conta è quella del circolo,
//    perché è lì che si va a giocare.*

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatCents, minutiDaOra, oraDaMinuti, type GiornoDellAgenda, type MiaAgenda } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { giornoDellaSettimana, giornoSenzaAnno, oggi, piuGiorni } from '../../lib/time.js';
import { ETICHETTA_TIPO, tacca } from './pezzi.js';
import { FoglioBasso } from './FoglioBasso.js';

/**
 * ⚖️ **Quindici indietro e quindici avanti** (voce 13, 23/09/2026 — erano sette
 * e sei). Indietro serve il registro: correggere chi c'era si fa di qui, e
 * sparita la riga «Lezioni passate» del mockup è la sola strada per il passato.
 * Avanti serve sapere com'è messo il mese che viene.
 * 📏 Trentuno giorni in tutto è **il tetto del server** (`miaAgendaQuerySchema`,
 *    `max(31)`): uno in più e la richiesta verrebbe rifiutata.
 */
const INDIETRO = 15;
const AVANTI = 15;

/** `2h30`, `45′`, `1h`. Sotto la tacca del datario ci stanno quattro caratteri. */
function oreEminuti(minuti: number): string {
  if (minuti === 0) return '';
  const ore = Math.floor(minuti / 60);
  const resto = minuti % 60;
  if (ore === 0) return `${resto}′`;
  return resto === 0 ? `${ore}h` : `${ore}h${String(resto).padStart(2, '0')}`;
}

/**
 * Come si chiama un giorno **rispetto a oggi**, e «oggi» è quello del circolo.
 *
 * ⚠️ *Ancora oggi* e non *Oggi* quando la giornata è cominciata: a bordo campo
 * alle sei di sera «Oggi» sopra un elenco che comincia alle nove di mattina fa
 * cercare una lezione che è già finita.
 */
function nomeDelGiorno(date: string, adessoDate: string, cominciato: boolean): string {
  if (date === adessoDate) return cominciato ? 'Ancora oggi' : 'Oggi';
  if (date === piuGiorni(adessoDate, -1)) return 'Ieri';
  if (date === piuGiorni(adessoDate, 1)) return 'Domani';
  /* 👁️ **Senza l'anno**, trovato guardando lo schermo: «martedì 22 settembre
     2026» è lungo 26 caratteri e a 320 px riempie la riga per dire una cosa che
     nessuno stava chiedendo — l'agenda copre due settimane, l'anno è sempre
     questo. 📌 *Un dato che non cambia mai dentro la schermata non informa:
     occupa.* */
  return `${giornoDellaSettimana(date)} ${giornoSenzaAnno(date)}`;
}

export function Agenda(): React.JSX.Element {
  /*
   * ⚠️ Il `dal` lo calcola il browser, e va bene che sia così: sceglie la
   * finestra, non la verità. Quale giorno è «Oggi» lo dice `adesso`, che arriva
   * dal server — ⇒ la testa di questo file.
   */
  const dal = piuGiorni(oggi(), -INDIETRO);

  const agenda = useQuery({
    queryKey: ['maestro', 'agenda', dal],
    queryFn: () => chiama<MiaAgenda>('GET', `/maestro/agenda?dal=${dal}&giorni=${INDIETRO + AVANTI + 1}`),
  });

  if (agenda.isLoading) return <Attesa cosa="la tua agenda" />;
  if (agenda.error) return <Guasto errore={agenda.error} />;
  if (!agenda.data) return <Attesa cosa="la tua agenda" />;
  return <Striscia agenda={agenda.data} />;
}

/**
 * 🚨 Il corpo sta in un componente a parte, e non è un vezzo: gli effetti che
 * portano la pagina su oggi e che tengono il datario in pari con lo scorrimento
 * hanno bisogno dei giorni **già disegnati**. Tenendoli nel componente che
 * aspetta la risposta girerebbero anche mentre non c'è niente, e il primo giro
 * utile — quello che deve centrare oggi — sarebbe uno dei tanti.
 */
function Striscia({ agenda }: { agenda: MiaAgenda }): React.JSX.Element {
  const { giorni, adesso } = agenda;
  const [inVista, setInVista] = useState(adesso.date);
  /** 🗓️ Il giorno vuoto appena toccato nel datario: si dice, e sparisce da sé. */
  const [nienteIl, setNienteIl] = useState<string | null>(null);
  /** ➕ Il foglio «Cosa prenoti?», aperto dal «+». */
  const [cosa, setCosa] = useState(false);
  const chiudiCosa = useCallback(() => setCosa(false), []);

  const sezioni = useRef(new Map<string, HTMLElement>());
  const datario = useRef<HTMLDivElement | null>(null);
  const cartello = useRef<HTMLDivElement | null>(null);
  const portato = useRef(false);

  /** Quanto copre la roba appiccicata in cima: la testata più il datario. */
  const quantoCopre = useCallback(() => {
    const testata = document.querySelector('.testata-maestro');
    return (
      (testata?.getBoundingClientRect().height ?? 0) + (datario.current?.getBoundingClientRect().height ?? 0)
    );
  }, []);

  /**
   * ⚓⭐ **L'ANCORA DI UN GIORNO: dov'è, per chi guarda e per chi ci salta.**
   *
   * Per tutti i giorni è la loro sezione. Per **oggi** è il blocco del confine
   * — la riga del passato più il cartello — perché quel blocco *appartiene* a
   * oggi: è lì che l'agenda si apre, ed è lì che si smette di leggere il
   * passato. Con due ancore diverse (una per saltare, una per sapere dove si
   * è) il datario segnerebbe **ieri** mentre a schermo c'è il confine di oggi
   * — ed è esattamente il difetto che la fotografia ha trovato.
   * 📌 *Se «dove sto» e «dove vado» si misurano su due cose diverse, prima o
   *    poi non coincidono — e a non coincidere è la cosa che si vede.*
   */
  const ancoraDi = useCallback(
    (date: string): HTMLElement | undefined =>
      date === adesso.date ? (cartello.current ?? sezioni.current.get(date)) : sezioni.current.get(date),
    [adesso.date],
  );

  const portaAlGiorno = useCallback(
    (date: string, subito: boolean) => {
      /* 👁️⭐⭐ **APRENDO SU OGGI CI SI FERMA AL CARTELLO, non al titolo del
         giorno — trovato guardando la fotografia, non rileggendo il codice.**

         Il cartello «adesso / la prossima» sta **sopra** i giorni, che è il suo
         posto: è l'unica cosa che non è un giorno. Ma l'agenda comincia sette
         giorni indietro e si apre su oggi ⇒ l'apertura lo lasciava **fuori
         schermo, sopra**, e per vederlo bisognava risalire sette giorni vuoti.
         📌 *La cosa più utile della schermata era anche l'unica che non si
            vedeva: non era nascosta, era sopra il punto in cui la pagina si
            apre.* */
      const meta = ancoraDi(date);
      /* 🗓️ Un giorno vuoto non ha sezione (voce 13): si segna nel datario e si
         dice che è vuoto. Senza, il tocco non farebbe niente — e da bordo
         campo un tocco che non fa niente si ripete più forte. */
      if (!meta) {
        if (!subito) {
          setInVista(date);
          setNienteIl(date);
        }
        return;
      }
      window.scrollTo({
        top: meta.getBoundingClientRect().top + window.scrollY - quantoCopre() - 8,
        behavior: subito ? 'auto' : 'smooth',
      });
      setInVista(date);
    },
    [quantoCopre, ancoraDi],
  );

  /*
   * 🎯 **SI APRE SU OGGI, e una volta sola.** L'agenda comincia sette giorni
   * indietro: senza questo salto si aprirebbe su lunedì scorso — cioè su un
   * elenco di cose già fatte, che è l'ultima a cui serve rispondere.
   * ⚠️ `useLayoutEffect` e non `useEffect`: il salto deve avvenire **prima** che
   *    il browser disegni, o si vede la pagina partire dal passato e scattare.
   */
  useLayoutEffect(() => {
    if (portato.current) return;
    portato.current = true;
    portaAlGiorno(adesso.date, true);
  }, [adesso.date, portaAlGiorno]);

  /*
   * 🔭⭐⭐ **IL DATARIO SEGUE LO SCORRIMENTO, e la regola è una riga sola:
   *    il giorno in vista è L'ULTIMO che è già cominciato sopra la riga.**
   *
   * 👁️ **Qui c'era un `IntersectionObserver`, e la fotografia l'ha bocciato.**
   * Guardava *chi tocca la fascia in alto* e prendeva il **primo**: aprendo su
   * oggi, il blocco del cartello spinge il titolo di oggi qualche pixel sotto
   * quella fascia, mentre la coda di **ieri** la tocca ancora ⇒ il datario
   * segnava «sab 19» con oggi a schermo. Un datario che indica un giorno
   * diverso da quello che si sta leggendo è peggio di nessun datario: fa
   * credere di essere altrove.
   * ⚖️ La regola nuova non ha margini da accordare: si scorre l'elenco dei
   * giorni e si tiene **l'ultimo** il cui titolo è già passato sotto la riga di
   * testata e datario. È quello che si sta leggendo, per definizione.
   * 📌 *Una soglia va scelta perché descrive la cosa, non perché su questo
   *    schermo dà il risultato giusto.*
   */
  useEffect(() => {
    let inCoda = 0;
    const guarda = () => {
      inCoda = 0;
      const riga = quantoCopre() + 24;
      /* ⚠️ Si scorre l'ELENCO dei giorni, non la mappa: l'ordine di una `Map`
         è quello in cui React ha agganciato i riferimenti, e a ogni ridisegno
         può non essere più quello del calendario. «L'ultimo» va letto su una
         cosa che è ordinata per davvero. */
      let quale: string | null = null;
      for (const g of giorni) {
        const ancora = ancoraDi(g.date);
        if (ancora && ancora.getBoundingClientRect().top <= riga) quale = g.date;
      }
      if (quale) setInVista(quale);
    };
    const alProssimoDisegno = () => {
      if (inCoda === 0) inCoda = requestAnimationFrame(guarda);
    };
    window.addEventListener('scroll', alProssimoDisegno, { passive: true });
    guarda();
    return () => {
      window.removeEventListener('scroll', alProssimoDisegno);
      if (inCoda !== 0) cancelAnimationFrame(inCoda);
    };
  }, [quantoCopre, giorni, ancoraDi]);

  /*
   * ⭐ La tacca attiva si porta **al centro** della striscia da sé. Senza, il
   * giorno in cui si sta finisce fuori schermo appena si scorre di qualche
   * giorno, e il datario mostra un pezzo di settimana che non c'entra.
   * ⚠️ Si sposta `scrollLeft` a mano invece di `scrollIntoView`: quello
   *    trascina anche la pagina in verticale, cioè combatte con lo scorrimento
   *    che ha appena causato questo aggiornamento.
   */
  useEffect(() => {
    const striscia = datario.current?.querySelector<HTMLElement>('.tacche');
    const attiva = striscia?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!striscia || !attiva) return;
    striscia.scrollLeft = attiva.offsetLeft - striscia.clientWidth / 2 + attiva.clientWidth / 2;
  }, [inVista]);

  useEffect(() => {
    if (nienteIl === null) return;
    const t = setTimeout(() => setNienteIl(null), 2200);
    return () => clearTimeout(t);
  }, [nienteIl]);

  const cominciato = adesso.minuti > 0;
  /* ⭐ Nell'elenco solo i giorni in cui c'è qualcosa, e **oggi sempre**: è il
     punto in cui l'agenda si apre, e senza la sua sezione non ci sarebbe
     nemmeno il confine fra il passato e il resto. */
  const nellElenco = useMemo(
    () => giorni.filter((g) => g.items.length > 0 || g.date === adesso.date),
    [giorni, adesso.date],
  );
  const passati = useMemo(
    () => nellElenco.filter((g) => g.date < adesso.date).length,
    [nellElenco, adesso.date],
  );

  return (
    <>
      <div className="datario-maestro" ref={datario}>
        <div className="tacche" role="tablist" aria-label="I giorni">
          {giorni.map((g) => {
            const t = tacca(g.date);
            return (
              <button
                key={g.date}
                type="button"
                role="tab"
                aria-current={g.date === inVista ? 'true' : undefined}
                className={[
                  g.date === adesso.date ? 'oggi' : '',
                  g.date < adesso.date ? 'passato' : '',
                  g.date === inVista ? 'scelto' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => portaAlGiorno(g.date, false)}
              >
                <span className="che-giorno">{t.giorno}</span>
                <span className="che-numero">{t.numero}</span>
                {/* Quanto è pieno quel giorno. Vuoto non scrive «0h»: uno zero
                    ripetuto quattordici volte è rumore, e il vuoto si vede. */}
                <span className="quanto">{oreEminuti(g.minutiOccupati)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {nellElenco.map((g) => (
        <Fragment key={g.date}>
          {/* Il confine fra ciò che è stato e ciò che viene: qui sopra finisce
              il passato, e qui comincia la parte su cui si può ancora fare
              qualcosa. È anche il punto in cui l'agenda si apre. */}
          {g.date === adesso.date && (
            <div ref={cartello} className="apertura-agenda">
              {passati > 0 && (
                <p className="tenue piccolo capo-passato">
                  Sopra c’è quello che è già passato: si corregge chi c’era, il resto no.
                </p>
              )}
              <CheCiStaAdesso giorni={giorni} adesso={adesso} />
            </div>
          )}
          <section
            className={`giorno-agenda${g.date < adesso.date ? ' passato' : ''}`}
            data-giorno={g.date}
            ref={(n) => {
              if (n) sezioni.current.set(g.date, n);
              else sezioni.current.delete(g.date);
            }}
          >
            <h2>
              {nomeDelGiorno(g.date, adesso.date, cominciato)}
              {g.minutiOccupati > 0 && <span className="tenue piccolo">{oreEminuti(g.minutiOccupati)}</span>}
            </h2>
            {g.items.length === 0 ? (
              <p className="tenue piccolo niente">Niente in campo.</p>
            ) : (
              <ul className="elenco-lezioni">
                {g.items.map((l) => (
                  <RigaLezione key={l.id} l={l} />
                ))}
              </ul>
            )}
          </section>
        </Fragment>
      ))}

      {nienteIl !== null && (
        <p className="avviso-giorno" role="status">
          Niente in campo {giornoDellaSettimana(nienteIl)} {giornoSenzaAnno(nienteIl)}
        </p>
      )}

      {/* ➕⭐ **IL «+» — voce 13.** Prende il posto della voce «Prenota» della
          barra, e sta solo qui: l'agenda è il posto da cui si decide di mettere
          qualcosa in calendario. Si nasconde col foglio aperto, o starebbe sopra
          il velo. */}
      {/* 💶⭐ **L'«€» — voce 16.** Lo stesso oggetto del «+», dal lato opposto,
          e apre «I soldi». 🗣️ *«alla sinistra del più, lo stesso oggetto
          solamente con il simbolo dell'euro»*. Prende il posto delle tre righe
          che stavano in fondo all'agenda (corsi, soldi, ore): i corsi si
          raggiungono dalla loro scheda, le assenze dal «+». Come il «+», si
          nasconde col foglio aperto. */}
      {!cosa && (
        <>
          <Link className="euro-maestro" to="/maestro/soldi" aria-label="I soldi">
            €
          </Link>
          <button type="button" className="piu-maestro" aria-label="Prenota" onClick={() => setCosa(true)}>
            {/* ✚ Disegnato, non scritto — 🗣️ *«il più non mi sembra centrato nel
                cerchio»*. Un «+» di testo sta dove lo mette il font del
                telefono, e ogni telefono ne ha uno suo: due tratti in un
                quadrato sono al centro per costruzione, dappertutto. */}
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
              <path d="M12 3.5v17M3.5 12h17" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
            </svg>
          </button>
        </>
      )}
      {cosa && <CosaPrenoti chiudi={chiudiCosa} />}
    </>
  );
}

/**
 * ➕⭐⭐ **«COSA PRENOTI?» — il primo passo, dentro un foglio.**
 *
 * Fino al 23/09 era una schermata («Prenota», voce della barra). Il mockup
 * approvato del 20/09 la voleva già così: il «+» apre un foglio, e le due strade
 * si dividono da qui — la lezione va a scegliere l'ora, il corso alla sua
 * schermata, che ha un passo in più (il nome, quante volte).
 * ⚖️ Il corso si porta dietro **da dove viene** (`da: 'agenda'`): così il suo
 *    «‹» riporta all'agenda e non a un elenco di corsi mai visto.
 */
function CosaPrenoti({ chiudi }: { chiudi: () => void }): React.JSX.Element {
  return (
    <FoglioBasso titolo="Cosa prenoti?" chiudi={chiudi}>
      <div className="scelta-grande">
        <Link to="/maestro/nuova">
          <strong>Una lezione</strong>
          <span className="tenue">Un’ora tua, e gli allievi si mettono dopo</span>
        </Link>
        {/* 🏋️ Voce 21: fra la lezione e il corso, l'ordine della tendina della
            segreteria. Nell'indirizzo e non in uno stato, così ricaricando
            la pagina resta un allenamento. */}
        <Link to="/maestro/nuova?tipo=allenamento">
          <strong>Un allenamento</strong>
          <span className="tenue">Un’ora e mezza, fino a quattro allievi</span>
        </Link>
        <Link to="/maestro/corsi/nuovo" state={{ da: 'agenda' }}>
          <strong>Un corso</strong>
          <span className="tenue">Un gruppo che torna, con gli stessi iscritti</span>
        </Link>
        {/* 🏖️ Voce 16: senza la riga «Quando puoi prenotare» in fondo
            all'agenda, la pagina delle assenze resterebbe senza ingresso. */}
        <Link to="/maestro/assenze">
          <strong>Un giorno in cui non ci sono</strong>
          <span className="tenue">In quei giorni il gestionale smette di proporti ore</span>
        </Link>
      </div>
    </FoglioBasso>
  );
}

/**
 * 🎯⭐ **IL CARTELLO: cosa c'è adesso, o cos'è la prossima.**
 *
 * ⚖️ È la risposta alla domanda che si fa un maestro tirando fuori il telefono,
 * e l'agenda da sola non ce l'ha: con quattordici giorni in fila, «cosa ho fra
 * mezz'ora» starebbe in mezzo a un elenco da scorrere.
 *
 * 🕰️ I minuti sono quelli del **circolo**: con l'orologio del browser una
 * lezione appena finita resterebbe «adesso» per chi ha il telefono indietro.
 */
function CheCiStaAdesso({
  giorni,
  adesso,
}: {
  giorni: readonly GiornoDellAgenda[];
  adesso: { date: string; minuti: number };
}): React.JSX.Element | null {
  const oggiQui = giorni.find((g) => g.date === adesso.date);

  /* 🔄⭐ **Una lezione che non è più sua non è «la prossima»** (tappa ⑦c): il
     cartello risponde a *«cosa ho adesso»*, e mandarlo a dire «alle 18:30
     Campo 2» per un'ora che tiene un altro maestro lo manderebbe **al campo**
     — che è esattamente il viaggio che questa tappa esiste per evitare.
     📌 *Un cartello che nomina una cosa che non è tua non informa: dirotta.* */
  const suoi = (g: GiornoDellAgenda): GiornoDellAgenda['items'] => g.items.filter((l) => l.passataA === null);

  const inCorso = suoi(oggiQui ?? { date: '', items: [], minutiOccupati: 0 }).find(
    (l) => minutiDaOra(l.time) <= adesso.minuti && minutiDaOra(l.time) + l.durationMinutes > adesso.minuti,
  );

  /* La prossima è la prima che comincia dopo adesso, **in tutti i giorni che
     vengono** e non solo oggi: alle dieci di sera la risposta giusta è quella
     di domani mattina, non «non hai più niente». */
  const prossima =
    inCorso === undefined
      ? giorni
          .filter((g) => g.date >= adesso.date)
          .flatMap((g) => suoi(g).map((l) => ({ g, l })))
          .find(({ g, l }) => g.date > adesso.date || minutiDaOra(l.time) > adesso.minuti)
      : undefined;

  if (!inCorso && !prossima) return null;
  const l = inCorso ?? prossima!.l;
  const quando = inCorso ? 'Adesso' : prossima!.g.date === adesso.date ? 'La prossima, oggi' : 'La prossima';

  return (
    <Link className={`cartello-adesso${inCorso ? ' in-corso' : ''}`} to={`/maestro/lezioni/${l.id}`}>
      <span className="etichetta">{quando}</span>
      <strong>
        {l.time}–{oraDaMinuti(minutiDaOra(l.time) + l.durationMinutes)}
        {!inCorso &&
          prossima!.g.date !== adesso.date &&
          ` · ${nomeDelGiorno(prossima!.g.date, adesso.date, false)}`}
      </strong>
      <span className="tenue">
        {ETICHETTA_TIPO[l.type] ?? l.type} · {l.courtName}
      </span>
      <span>
        {l.players.length === 0 ? 'nessun allievo' : l.players.map((g) => g.displayName).join(' · ')}
      </span>
    </Link>
  );
}

/**
 * Una riga dell'elenco. Era dentro `Oggi`, ed è uscita perché adesso la usano
 * quattordici giorni invece di uno.
 *
 * 🔗 Tutta la riga è il bersaglio, non una parolina «apri»: con un pollice si
 * prende quello che è grande (⇒ `base.css`, 44 px).
 */
function RigaLezione({ l }: { l: GiornoDellAgenda['items'][number] }): React.JSX.Element {
  /* 🔄 Non è più sua: la riga resta, ma si spegne e porta il motivo. */
  const altrui = l.passataA !== null;
  return (
    <li>
      <Link className={altrui ? 'ferma' : undefined} to={`/maestro/lezioni/${l.id}`}>
        <strong>
          {l.time}–{oraDaMinuti(minutiDaOra(l.time) + l.durationMinutes)}
          {/* 🔄⭐⭐ **LE DUE PASTIGLIE DELLA TAPPA ⑦c.** «non è più tua» è la
              cosa che, non detta, si scopre **andando al campo**: senza,
              quella riga sparirebbe e basta. «nuova per te» è il verso
              opposto, e serve per la stessa ragione letta al contrario — senza,
              in agenda comparirebbe una lezione che il maestro giurerebbe di
              non aver mai prenotato. */}
          {altrui ? (
            <span className="pastiglia ferma">non è più tua</span>
          ) : l.arrivataDa !== null ? (
            <span className="pastiglia nuova">nuova per te</span>
          ) : null}
        </strong>
        <span className="tenue">
          {ETICHETTA_TIPO[l.type] ?? l.type} · {l.courtName}
        </span>
        <span>
          {altrui
            ? `adesso la tiene ${l.passataA!}`
            : l.players.length === 0
              ? 'nessun allievo'
              : l.players.map((g) => g.displayName).join(' · ')}
        </span>
        {/* 💶 A carico, mai «incassato»: da questa porta non passa cassa.
            ⛔ E non su una che non è più sua: quei soldi non sono più suoi, e
               una cifra lì sotto direbbe il contrario di quello che la riga
               sopra ha appena detto. */}
        {!altrui && l.players.length > 0 && (
          <span className="tenue piccolo">a carico {formatCents(l.totalDueCents)}</span>
        )}
      </Link>
    </li>
  );
}
